/**
 * Et lite tekstdokument, delt mellom nettflata og appen.
 *
 * HTML KRYSSER ALDRI. Redigeringsfeltet på nettflata er `contenteditable`, og
 * det produserer hva som helst — særlig når noen limer inn fra et annet
 * program. I stedet for å vaske en streng i etterkant GÅR VI GJENNOM NODENE
 * og slipper bare igjennom det vi kjenner igjen. Det som ikke gjenkjennes
 * blir ren tekst.
 *
 * Det er forskjellen på «vi har prøvd å fjerne det farlige» og «det farlige
 * kan ikke komme inn». Appen bygger sine egne elementer fra modellen og
 * setter aldri markup den har fått utenfra.
 *
 * Settet er lite med vilje: overskrift, underoverskrift, avsnitt, punkt — og
 * kursiv, understrek, gjennomstreking og farge inne i dem. En beskjed til
 * crewet trenger ikke mer, og hvert tillegg er noe begge flatene må tegne.
 */

/** Fargene er en fast palett, ikke en fri velger. Som hurtigknappene. */
export const FARGER = ["rod", "gul", "gronn", "bla"] as const;
export type Farge = (typeof FARGER)[number];

export interface Bit {
  readonly tekst: string;
  readonly fet?: boolean;
  readonly kursiv?: boolean;
  readonly understrek?: boolean;
  readonly gjennomstrek?: boolean;
  readonly farge?: Farge;
}

export type BlokkType =
  | "overskrift" | "underoverskrift" | "avsnitt" | "punkt" | "huskepunkt";

export interface Blokk {
  readonly type: BlokkType;
  readonly biter: readonly Bit[];
  /**
   * Bare for `huskepunkt`: om det er gjort.
   *
   * DELT, ikke personlig. Huker A-foto av «lade batterier», ser hele crewet
   * at det er gjort — det er oppfoerselen en crewliste skal ha. Haken ligger
   * derfor i dokumentet, ikke paa telefonen.
   */
  readonly gjort?: boolean;
}

export type Tekstdok = readonly Blokk[];

export const erFarge = (v: string): v is Farge =>
  (FARGER as readonly string[]).includes(v);

/**
 * Usynlig tegn redigeringsfeltet bruker som holdeplass.
 *
 * Velger man en farge FØR man skriver, må det finnes noe å sette markøren i
 * — et tomt element holder den ikke. Tegnet lever bare i feltet og vaskes
 * bort her, så det aldri havner i et dokument eller i en rapport.
 */
const USYNLIG = /\u200B/g;

/** Tomt dokument. Ett tomt avsnitt, så feltet har noe å sette markøren i. */
export function tomtDok(): Tekstdok {
  return [{ type: "avsnitt", biter: [] }];
}

export function erTom(dok: Tekstdok): boolean {
  return dok.every((b) => b.biter.every((i) => i.tekst.trim().length === 0));
}

/**
 * Samler huskepunktene nederst, i den rekkefølgen de ble skrevet.
 *
 * ET DOKUMENT MAN SKAL GÅ GJENNOM, ikke bare lese. Ligger punktene strødd
 * mellom avsnittene, må man lete etter dem — og det er nettopp det man ikke
 * har tid til når man går gjennom lista mellom to opptak. Teksten først,
 * det som skal gjøres samlet til slutt.
 *
 * Kjøres når feltet forlates, ikke mens man skriver: en linje som hopper
 * nedover mens fingeren står på tastaturet er verre enn en som ligger feil
 * et øyeblikk.
 */
export function sorterHuskepunkt(dok: Tekstdok): Tekstdok {
  const tekst = dok.filter((b) => b.type !== "huskepunkt");
  const huske = dok.filter((b) => b.type === "huskepunkt");
  return huske.length === 0 ? dok : [...tekst, ...huske];
}

/**
 * Et nytt huskepunkt, bakerst.
 *
 * Uten tekst blir det en TOM blokk, ikke en blokk med en tom bit. Forskjellen
 * er ikke teoretisk: en blokk som ser ut som den har innhold faar ikke
 * `<br>`-en som holder linja aapen, og da kan man ikke klikke i den.
 */
export function medHuskepunkt(dok: Tekstdok, tekst: string): Tekstdok {
  const biter = tekst.length === 0 ? [] : [{ tekst }];
  return [...dok, { type: "huskepunkt", biter, gjort: false }];
}

/** Snur haken på ett huskepunkt. Posisjonen er nøkkelen — dokumentet er en liste. */
export function medHake(dok: Tekstdok, i: number, gjort: boolean): Tekstdok {
  return dok.map((b, j) => (j === i && b.type === "huskepunkt" ? { ...b, gjort } : b));
}

/** Ren tekst, til forhåndsvisning og til rapporten. */
export function tilTekst(dok: Tekstdok): string {
  return dok
    .map((b) => {
      const t = b.biter.map((i) => i.tekst).join("");
      if (b.type === "punkt") return `• ${t}`;
      if (b.type === "huskepunkt") return `${b.gjort === true ? "[x]" : "[ ]"} ${t}`;
      return t;
    })
    .join("\n");
}

/* ---- lagring ---------------------------------------------------------- */

/**
 * Leser et dokument fra noe som er lagret.
 *
 * Alt som ikke ser riktig ut blir forkastet, ikke reparert. En halvveis
 * tolket beskjed er verre enn en tom: den som leser ville ikke visst at noe
 * manglet.
 */
export function fraLagret(v: unknown): Tekstdok {
  if (!Array.isArray(v)) return tomtDok();
  const ut: Blokk[] = [];
  for (const rå of v) {
    if (typeof rå !== "object" || rå === null) continue;
    const o = rå as Record<string, unknown>;
    const type = String(o["type"]);
    if (!["overskrift", "underoverskrift", "avsnitt", "punkt", "huskepunkt"]
      .includes(type)) continue;
    const biter: Bit[] = [];
    if (Array.isArray(o["biter"])) {
      for (const rb of o["biter"]) {
        if (typeof rb !== "object" || rb === null) continue;
        const b = rb as Record<string, unknown>;
        /*
         * Tomme biter forkastes.
         *
         * En blokk med ÉN tom bit ser ut som den har innhold, og da fikk
         * den ikke `<br>`-en som gjoer at man kan klikke i den. Et
         * huskepunkt man ikke kunne skrive i kom derfra. `slaaSammen` gjoer
         * det samme naar dokumentet leses fra feltet; her gjelder det det
         * som kommer fra lagringen.
         */
        if (typeof b["tekst"] !== "string" || b["tekst"].length === 0) continue;
        biter.push(lagBit(b["tekst"], {
          fet: b["fet"] === true,
          kursiv: b["kursiv"] === true,
          understrek: b["understrek"] === true,
          gjennomstrek: b["gjennomstrek"] === true,
          farge: typeof b["farge"] === "string" && erFarge(b["farge"]) ? b["farge"] : undefined,
        }));
      }
    }
    /* `utenHale` ogsaa her, ikke bare paa det som leses fra feltet: det
       ligger allerede lagrede blokker som bare inneholder «\n». De leges naar
       de leses, slik at en linje som er tom blir tom ogsaa i modellen. */
    const blokk: { -readonly [K in keyof Blokk]: Blokk[K] } =
      { type: type as BlokkType, biter: utenHale(biter) };
    if (type === "huskepunkt") blokk.gjort = o["gjort"] === true;
    ut.push(blokk);
  }
  return ut.length > 0 ? ut : tomtDok();
}

interface Stil {
  readonly fet: boolean;
  readonly kursiv: boolean;
  readonly understrek: boolean;
  readonly gjennomstrek: boolean;
  readonly farge: Farge | undefined;
}

const INGEN: Stil = {
  fet: false, kursiv: false, understrek: false, gjennomstrek: false,
  farge: undefined,
};

/** Bare satte merker lagres. Et dokument fullt av `false` er bare støy. */
function lagBit(tekst: string, s: Partial<Stil>): Bit {
  const b: { -readonly [K in keyof Bit]: Bit[K] } = { tekst };
  if (s.fet === true) b.fet = true;
  if (s.kursiv === true) b.kursiv = true;
  if (s.understrek === true) b.understrek = true;
  if (s.gjennomstrek === true) b.gjennomstrek = true;
  if (s.farge !== undefined) b.farge = s.farge;
  return b;
}

/* ---- fra redigeringsfeltet -------------------------------------------- */

const BLOKK_FOR_TAG: Readonly<Record<string, BlokkType>> = {
  H2: "overskrift",
  H3: "underoverskrift",
  LI: "punkt",
  P: "avsnitt",
  DIV: "avsnitt",
};

/**
 * Leser `contenteditable` og gir et dokument.
 *
 * Går gjennom nodene og slipper bare igjennom det vi kjenner. Limer noen inn
 * en hel nettside, blir det tekst med de merkene vi har — ikke fremmed
 * markup vi må stole på.
 */
export function fraNoder(rot: Element): Tekstdok {
  const ut: Blokk[] = [];
  gaa(rot, ut);
  return ut.length > 0 ? ut : tomtDok();
}

/** Elementer som åpner en ny blokk. Alt annet er innhold i en. */
const ER_BLOKK = "ul,ol,p,div,h1,h2,h3,h4,h5,h6,li,blockquote,pre";

/**
 * Går gjennom nodene og skriver blokker.
 *
 * TRE TING MÅ HÅNDTERES, og alle tre kommer fra `contenteditable`:
 *
 * 1. LØS TEKST. Nettlesere legger ofte første linje uten noen omsluttende
 *    blokk. Uten dette ble første avsnitt borte.
 * 2. LISTER INNI AVSNITT. `insertUnorderedList` produserer `<p><ul><li>` —
 *    ugyldig HTML, men det er det vi får. Flatet vi ut avsnittet, mistet
 *    punktene kulene sine.
 * 3. ALT ANNET. Et element vi ikke kjenner blir innholdet sitt, ikke borte.
 */
function gaa(el: Element, ut: Blokk[]): void {
  const loese: Bit[] = [];
  const toem = (): void => {
    const b = utenHale(slaaSammen(loese));
    loese.length = 0;
    if (b.length > 0) ut.push({ type: "avsnitt", biter: b });
  };

  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType === 3 /* TEXT_NODE */) {
      const t = (n.textContent ?? "").replace(USYNLIG, "");
      if (t.trim().length > 0) loese.push(lagBit(t, INGEN));
      continue;
    }
    if (n.nodeType !== 1 /* ELEMENT_NODE */) continue;
    const e = n as Element;
    const tag = e.tagName;

    if (tag === "UL" || tag === "OL") {
      toem();
      gaa(e, ut);
      continue;
    }

    const type = BLOKK_FOR_TAG[tag];
    if (type !== undefined) {
      toem();
      // Inneholder blokka en liste, må vi inn i den framfor å flate den ut.
      if (e.querySelector("ul,ol") !== null) {
        gaa(e, ut);
        continue;
      }
      const biter: Bit[] = [];
      samle(e, INGEN, biter);
      /*
       * Et huskepunkt er en `li` med vaart eget attributt. Uten det ville
       * det vaert et vanlig punkt — og da hadde haken vaert borte.
       */
      const huke = e.getAttribute("data-huske");
      if (type === "punkt" && huke !== null) {
        ut.push({
          type: "huskepunkt", biter: utenHale(slaaSammen(biter)), gjort: huke === "1",
        });
        continue;
      }
      ut.push({ type, biter: utenHale(slaaSammen(biter)) });
      continue;
    }

    // Et element uten blokker i seg er innhold: det hører til teksten rundt.
    if (e.querySelector(ER_BLOKK) === null) {
      samle(e, INGEN, loese);
      continue;
    }
    toem();
    gaa(e, ut);
  }
  toem();
}

function samle(el: Element, arv: Stil, ut: Bit[]): void {
  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType === 3 /* TEXT_NODE */) {
      const t = (n.textContent ?? "").replace(USYNLIG, "");
      if (t.length > 0) ut.push(lagBit(t, arv));
      continue;
    }
    if (n.nodeType !== 1 /* ELEMENT_NODE */) continue;
    const e = n as Element;
    if (e.tagName === "BR") {
      ut.push(lagBit("\n", arv));
      continue;
    }
    samle(e, merkeFor(e, arv), ut);
  }
}

function merkeFor(e: Element, arv: Stil): Stil {
  const tag = e.tagName;
  const stil = (e as HTMLElement).style;
  /*
   * Fet kjennes ogsaa paa VEKTEN, ikke bare paa `<b>`.
   *
   * Limer man inn fra et tekstbehandlingsprogram, kommer fet skrift som
   * `<span style="font-weight:700">`. Uten dette ville den vaert borte.
   */
  const vekt = Number.parseInt(stil.fontWeight, 10);
  return {
    fet: arv.fet || tag === "B" || tag === "STRONG"
      || stil.fontWeight === "bold" || (Number.isFinite(vekt) && vekt >= 600),
    kursiv: arv.kursiv || tag === "I" || tag === "EM" || stil.fontStyle === "italic",
    understrek: arv.understrek || tag === "U"
      || stil.textDecorationLine.includes("underline"),
    gjennomstrek: arv.gjennomstrek || tag === "S" || tag === "STRIKE" || tag === "DEL"
      || stil.textDecorationLine.includes("line-through"),
    farge: fargeFor(e) ?? arv.farge,
  };
}

/** Fargen leses av vårt eget attributt. En fri CSS-farge er ikke en farge her. */
function fargeFor(e: Element): Farge | undefined {
  const v = e.getAttribute("data-farge");
  return v !== null && erFarge(v) ? v : undefined;
}

/** Nabobiter med samme merker blir én. Ellers vokser dokumentet av ingenting. */
function slaaSammen(biter: readonly Bit[]): Bit[] {
  const ut: Bit[] = [];
  for (const b of biter) {
    const forrige = ut[ut.length - 1];
    if (forrige !== undefined && sammeMerker(forrige, b)) {
      ut[ut.length - 1] = { ...forrige, tekst: forrige.tekst + b.tekst };
      continue;
    }
    ut.push(b);
  }
  return ut.filter((b) => b.tekst.length > 0);
}

/**
 * Fjerner linjeskiftet nettleseren legger bakerst i en blokk.
 *
 * HER SATT FEILEN som gjorde at et tomt huskepunkt ikke kunne skrives i.
 * `tilNoder` legger en `<br>` i en tom linje for at den skal kunne ta imot
 * markøren. Leses den tilbake, blir den til «\n» — og da ser blokka ut som
 * den har innhold. Neste gang den tegnes får den ingen `<br>`, linja får
 * null høyde, og man kan huke den av men ikke klikke i den.
 *
 * Det forklarer også hvorfor den bare kom «noen ganger»: først etter at
 * dokumentet hadde vært innom lagringen.
 *
 * Bare det BAKERSTE skiftet fjernes. Et linjeskift midt i en blokk er noe
 * noen har satt selv.
 */
function utenHale(biter: readonly Bit[]): Bit[] {
  const ut = biter.slice();
  const sist = ut[ut.length - 1];
  if (sist === undefined || !sist.tekst.endsWith("\n")) return ut;
  const kortere = sist.tekst.slice(0, -1);
  if (kortere.length === 0) ut.pop();
  else ut[ut.length - 1] = { ...sist, tekst: kortere };
  return ut;
}

const sammeMerker = (a: Bit, b: Bit): boolean =>
  a.fet === b.fet && a.kursiv === b.kursiv && a.understrek === b.understrek
  && a.gjennomstrek === b.gjennomstrek && a.farge === b.farge;

/* ---- til redigeringsfeltet -------------------------------------------- */

const TAG_FOR_BLOKK: Readonly<Record<BlokkType, string>> = {
  overskrift: "h2",
  underoverskrift: "h3",
  avsnitt: "p",
  punkt: "li",
  huskepunkt: "li",
};

/**
 * Bygger nodene et dokument består av.
 *
 * Elementene LAGES, de settes ikke inn som en streng. Da finnes det ingen
 * vei fra lagret innhold til markup vi ikke har skrevet selv.
 */
export function tilNoder(dok: Tekstdok, doc: Document): DocumentFragment {
  const ut = doc.createDocumentFragment();
  let liste: Element | null = null;

  for (const blokk of dok) {
    const el = doc.createElement(TAG_FOR_BLOKK[blokk.type]);
    if (blokk.type === "huskepunkt") {
      el.setAttribute("data-huske", blokk.gjort === true ? "1" : "0");
    }
    for (const bit of blokk.biter) el.appendChild(bitNode(bit, doc));
    /*
     * En linje uten synlig tekst maa kunne ta imot markoeren.
     *
     * Maalt paa ANTALL TEGN og ikke paa antall biter: en blokk kan ha biter
     * som alle er tomme, og da staar linja like aapen som om den ikke hadde
     * noen. Uten `<br>`-en har `li`-en ingen hoeyde aa klikke i — man kan
     * huke den av, men ikke skrive i den.
     */
    if (blokk.biter.every((b) => b.tekst.length === 0)) {
      el.appendChild(doc.createElement("br"));
    }

    if (blokk.type === "punkt" || blokk.type === "huskepunkt") {
      /*
       * ÉN liste for kulepunkter og huskepunkter som foelger etter
       * hverandre. Hva slags linje det er staar paa `li`-en.
       *
       * Det stod to lister her foer, og huskelista bar et merke som
       * overskriften «Huskeliste» ble tegnet paa. Det holdt ikke: sletter
       * man det siste huskepunktet, slaar nettleseren de to listene
       * sammen av seg selv, og den som blir staaende tar merket med seg.
       * Da havnet overskriften over et vanlig kulepunkt.
       *
       * Et merke paa LISTA er noe nettleseren kan flytte paa mens man
       * skriver. `data-huske` paa LINJA er det ikke — den foelger linja
       * uansett hvilken liste den havner i. Overskriften henger derfor paa
       * det foerste huskepunktet, og listene trenger ikke vaere delt.
       */
      if (liste === null) {
        liste = doc.createElement("ul");
        ut.appendChild(liste);
      }
      liste.appendChild(el);
      continue;
    }
    liste = null;
    ut.appendChild(el);
  }
  return ut;
}

function bitNode(bit: Bit, doc: Document): Node {
  const tekst = doc.createTextNode(bit.tekst);
  let node: Node = tekst;
  if (bit.fet === true) node = pakk("b", node, doc);
  if (bit.kursiv === true) node = pakk("i", node, doc);
  if (bit.understrek === true) node = pakk("u", node, doc);
  if (bit.gjennomstrek === true) node = pakk("s", node, doc);
  if (bit.farge !== undefined) {
    const span = doc.createElement("span");
    span.setAttribute("data-farge", bit.farge);
    span.appendChild(node);
    node = span;
  }
  return node;
}

function pakk(tag: string, inni: Node, doc: Document): Node {
  const el = doc.createElement(tag);
  el.appendChild(inni);
  return el;
}
